import { useCallback, useMemo } from 'react';

import { useViewModeTransition } from '../../../context/ViewModeTransitionContext.tsx';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import i18n from '../../../i18n';
import {
  cancelExclusionDraw,
  setPlaneDrawActive,
  setPointsView,
} from '../../../store/reducers/measurementPlanes.reducer.ts';
import { toastReducer } from '../../../store/reducers/toast.reducer';
import {
  addLamp,
  cancelAutoPositioning,
  clearLampDistanceSelection,
  clearMeasurementPoints,
  duplicateVersion,
  setActiveToolbarTool,
  setShowAutoPositionSidebar,
  setTargetCameraPosition,
  startAutoPositioning,
  startTamanOptimization,
} from '../../../store/reducers/workspace.reducer.ts';
import { selectActiveVersion } from '../../../store/selectors/workspace.selector.ts';
import { DISTANCE_TO_GROUND_NA } from './types';

/** The workspace's main actions, shared by the footer and the panel sections. Same rules
 *  (and toasts) as the legacy sidebar's handlers. */
export function useWorkspaceActions() {
  const dispatch = useAppDispatch();
  const lamps = useAppSelector((s) => s.workspace.lamps);
  const modelLayers = useAppSelector((s) => s.workspace.modelLayers);
  const activeToolbarTool = useAppSelector((s) => s.workspace.activeToolbarTool);
  const desiredLuminaireHeight = useAppSelector((s) => s.workspace.desiredLuminaireHeight);
  const projectId = useAppSelector((s) => s.workspace.project?.id);
  const isCreatingVersion = useAppSelector((s) => s.workspace.isCreatingVersion);
  const activeVersion = useAppSelector(selectActiveVersion);
  const { transitionToMode } = useViewModeTransition();

  const startAutoposition = useCallback(() => {
    if (!modelLayers.some((layer) => layer.canHoldLuminaire)) {
      dispatch(
        toastReducer.actions.showToast({ message: 'workspace.toast.atLeastOneLayerLuminaireEnabled', type: 'warning' }),
      );
      return;
    }
    dispatch(startAutoPositioning());
  }, [dispatch, modelLayers]);

  const cancelAutoposition = useCallback(() => {
    dispatch(cancelAutoPositioning());
  }, [dispatch]);

  const startOptimization = useCallback(() => {
    if (lamps.length === 0) {
      dispatch(toastReducer.actions.showToast({ message: 'workspace.toast.atLeastOneLampRequired', type: 'warning' }));
      return;
    }
    const notAboveModel = lamps.filter((lamp) => lamp.distanceToGround === DISTANCE_TO_GROUND_NA);
    if (notAboveModel.length > 0) {
      const lampNames = notAboveModel.map((lamp) => lamp.name).join(', ');
      dispatch(
        toastReducer.actions.showToast({
          message: i18n.t('workspace.toast.lampsNotAboveModel', { lampNames }),
          type: 'warning',
        }),
      );
      return;
    }
    dispatch(startTamanOptimization());
  }, [dispatch, lamps]);

  /** Adds a luminaire at the origin and flies the camera above it. */
  const addLampManually = useCallback(() => {
    if (activeToolbarTool === 'measurement') {
      dispatch(clearMeasurementPoints());
      dispatch(setActiveToolbarTool('selection'));
    }
    if (activeToolbarTool === 'lampDistance') {
      dispatch(clearLampDistanceSelection());
      dispatch(setActiveToolbarTool('selection'));
    }
    dispatch(
      addLamp({
        name: `Luminaire ${lamps.length + 1}`,
        position: [0, 0, 0] as [number, number, number],
        distanceToGround: desiredLuminaireHeight,
      }),
    );
    dispatch(setTargetCameraPosition({ position: [0, 0, 20], lookAt: [0, 0, 0] }));
    dispatch(setShowAutoPositionSidebar(false));
  }, [dispatch, activeToolbarTool, lamps.length, desiredLuminaireHeight]);

  /** A version with a calculation job can't run again, so a retry runs in a copy of it. */
  const retryInNewVersion = useCallback(() => {
    if (projectId == null || !activeVersion || isCreatingVersion) return;
    dispatch(duplicateVersion({ projectId, sourceVersionId: activeVersion.id }));
  }, [dispatch, projectId, activeVersion, isCreatingVersion]);

  /** Surfaces are drawn in the measurement view with the surface points shown. */
  const startSurfaceDrawing = useCallback(() => {
    transitionToMode('measurement');
    dispatch(setPointsView('surface'));
    dispatch(setPlaneDrawActive(true));
  }, [dispatch, transitionToMode]);

  const cancelSurfaceDrawing = useCallback(() => {
    dispatch(setPlaneDrawActive(false));
    dispatch(cancelExclusionDraw());
  }, [dispatch]);

  return useMemo(
    () => ({
      startAutoposition,
      cancelAutoposition,
      startOptimization,
      addLampManually,
      retryInNewVersion,
      startSurfaceDrawing,
      cancelSurfaceDrawing,
    }),
    [
      startAutoposition,
      cancelAutoposition,
      startOptimization,
      addLampManually,
      retryInNewVersion,
      startSurfaceDrawing,
      cancelSurfaceDrawing,
    ],
  );
}
